import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box, Paper } from '@material-ui/core';
import { useSearchExperience } from '../../search-experience-v2/api/useSearchExperience';
import ColorPicker from '../../../components/kea/ColorPicker';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import styles from './ThemeSettings.module.scss';
import IntlMessages from 'util/IntlMessages';

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

const ThemeHexInput = ({ colorKey, label, value, onChange, className }) => {
  const normalizedValue = value.toUpperCase();
  const [draft, setDraft] = useState(normalizedValue);

  useEffect(() => setDraft(normalizedValue), [normalizedValue]);

  return (
    <input
      className={className}
      data-testid={`theme-color-${colorKey}-hex`}
      aria-label={label}
      value={draft}
      maxLength={7}
      spellCheck={false}
      onChange={(event) => {
        const nextValue = event.target.value;
        setDraft(nextValue);
        if (HEX_COLOR.test(nextValue)) onChange(colorKey, nextValue.toLowerCase());
      }}
      onBlur={() => {
        if (!HEX_COLOR.test(draft)) setDraft(normalizedValue);
      }}
    />
  );
};

ThemeHexInput.propTypes = {
  colorKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string.isRequired,
};

/**
 * Theme Settings Tab - Color configuration for search experience
 * Reuses ThemeStep logic from Integration Wizard
 */
export const ThemePalette = ({ theme, onChange }) => {
  const localizeMessage = useLocalizedMessage();
  const colors = [
    {
      key: 'textHeader',
      title: localizeMessage('search.settings.theme.headersColor') || 'Headers Color',
      description: "ui.searchSettings.themeSettings.headingsAndProminentProductTitles",
      defaultValue: '#2b2b2b',
    },
    {
      key: 'textMain',
      title: localizeMessage('search.settings.theme.mainColor') || 'Main Color',
      description: "ui.searchSettings.themeSettings.bodyTextAndSupportingInformation",
      defaultValue: '#3a3a3a',
    },
    {
      key: 'mainAccent',
      title: localizeMessage('search.settings.theme.accentColor') || 'Accent Color',
      description: "ui.searchSettings.themeSettings.selectedStatesLinksAndPrimaryEmphasis",
      defaultValue: '#121a4b',
    },
    {
      key: 'secondAccent',
      title: localizeMessage('search.settings.theme.buttonColor') || 'Button Color',
      description: "ui.searchSettings.themeSettings.primaryStorefrontActions",
      defaultValue: '#a22d2d',
    },
    {
      key: 'borderColor',
      title: localizeMessage('search.settings.theme.borderColor') || 'Border Color',
      description: "ui.searchSettings.themeSettings.dividersOutlinesAndFieldBoundaries",
      defaultValue: '#cfcfcf',
    },
    {
      key: 'backgroundColor',
      title: localizeMessage('search.settings.theme.backgroundColor') || 'Background Color',
      description: "ui.searchSettings.themeSettings.searchSurfacesAndProductCardBackgrounds",
      defaultValue: '#ffffff',
    },
  ];

  return (
    <Box className={styles.root}>
      <Paper className={styles.card} elevation={0}>
        <Box className={styles.header}>
          <h2 className={styles.title}><IntlMessages id="ui.searchSettings.themeSettings.searchInterfaceColors" /></h2>
          <p className={styles.description}>
            <IntlMessages id="ui.searchSettings.themeSettings.customizeTheSharedPaletteUsedBySearchResultsControlsAnd" />
          </p>
        </Box>
        <div className={styles.palette} data-testid="theme-color-palette">
          {colors.map(({ key, title, description, defaultValue }) => {
            const value = theme[key] || defaultValue;
            return (
              <div className={styles.colorItem} key={key} data-testid={`theme-color-${key}`}>
                <div className={styles.colorText}>
                  <h3 className={styles.colorTitle}>{title}</h3>
                  <p className={styles.colorDescription}>{localizeMessage(description)}</p>
                </div>
                <div className={styles.colorControl}>
                  <ThemeHexInput
                    className={styles.colorValue}
                    colorKey={key}
                    label={localizeMessage('ui.searchSettings.themeSettings.hexColorLabel', { title })}
                    value={value.toUpperCase()}
                    onChange={onChange}
                  />
                  <div className={styles.swatch}>
                    <ColorPicker
                      value={value}
                      label={localizeMessage('ui.searchSettings.themeSettings.colorPickerLabel', { title })}
                      onChange={nextValue => onChange(key, nextValue)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Paper>
    </Box>
  );
};

ThemePalette.propTypes = {
  theme: PropTypes.shape({
    textHeader: PropTypes.string,
    textMain: PropTypes.string,
    mainAccent: PropTypes.string,
    secondAccent: PropTypes.string,
    borderColor: PropTypes.string,
    backgroundColor: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
};

ThemePalette.defaultProps = {
  theme: {},
};

const ThemeSettings = ({ client, shopId, onRegisterRef, onHasChanges }) => {
  const currentShop = useSelector((s) => s.app.currentShop);
  const effectiveShopId = shopId || currentShop;

  const {
    config,
    updateTheme,
    loadConfig,
    saveConfig,
    error,
  } = useSearchExperience(client, effectiveShopId);

  useEffect(() => {
    if (!config && !error) loadConfig('preview');
  }, [config, error, loadConfig]);

  useEffect(() => {
    if (onRegisterRef) {
      onRegisterRef({
        save: saveConfig,
        reload: async () => loadConfig('preview'),
      });
    }
  }, [onRegisterRef, saveConfig, loadConfig]);

  if (error) {
    return <Box p={4} color="error.main"><IntlMessages id="ui.searchSettings.themeSettings.errorLoadingTheme" />{' '}{error}</Box>;
  }
  if (!config) return <Box p={4}><IntlMessages id="ui.searchSettings.themeSettings.loadingThemeConfiguration" /></Box>;

  return (
    <ThemePalette
      theme={config.search?.theme || {}}
      onChange={(key, value) => {
        updateTheme({ [key]: value });
        if (onHasChanges) onHasChanges(true);
      }}
    />
  );
};

ThemeSettings.propTypes = {
  client: PropTypes.shape({
    loadSearchExperienceConfig: PropTypes.func.isRequired,
    saveSearchExperienceConfig: PropTypes.func.isRequired,
  }).isRequired,
  shopId: PropTypes.string,
  onRegisterRef: PropTypes.func,
  onHasChanges: PropTypes.func,
};

ThemeSettings.defaultProps = {
  shopId: null,
  onRegisterRef: null,
  onHasChanges: null,
};

export default ThemeSettings;
